import React from 'react';
import { AlertCircle } from 'lucide-react';
import type {
  DetectionResult,
  DetectMessage,
  CleanMessage,
  WorkerResponse,
  DashHandlingOptions,
} from '../workers/watermarkDetection.types';

/**
 * Watermark Detection Utility - Worker-based Implementation
 *
 * ALL heavy detection logic now runs in a Web Worker to keep the main thread responsive.
 * This utility provides a clean interface for components to use worker-based detection.
 *
 * ZERO synchronous detection logic remains in this file.
 */

// Lazy-initialize worker only on client side
let worker: Worker | null = null;
let workerInitialized = false;
let requestIdCounter = 0;
const pendingRequests = new Map<
  string,
  {
    resolve: (result: DetectionResult) => void;
    reject: (error: Error) => void;
  }
>();

/**
 * Initialize the Web Worker (client-side only)
 */
function initWorker(): void {
  if (workerInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    // Use Vite's Web Worker support
    worker = new Worker(
      new URL('../workers/watermarkDetection.worker.ts', import.meta.url),
      { type: 'module' }
    );

    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const message = e.data;

      if (message.type === 'RESULT') {
        const pending = pendingRequests.get(message.requestId);
        if (pending) {
          pending.resolve(message.result);
          pendingRequests.delete(message.requestId);
        }
      } else if (message.type === 'ERROR') {
        const pending = pendingRequests.get(message.requestId);
        if (pending) {
          pending.reject(new Error(message.error));
          pendingRequests.delete(message.requestId);
        }
      }
    };

    worker.onerror = (error) => {
      console.error('[WatermarkWorker] Worker error:', error);
      // Reject all pending requests
      pendingRequests.forEach(({ reject }) => {
        reject(new Error('Worker error'));
      });
      pendingRequests.clear();
    };

    workerInitialized = true;
  } catch (error) {
    console.error('[WatermarkWorker] Failed to initialize worker:', error);
  }
}

/**
 * Terminate the Web Worker (cleanup)
 */
export function terminateWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
    workerInitialized = false;
    pendingRequests.clear();
  }
}

/**
 * Send a message to the worker and get a promise for the result
 */
function sendWorkerMessage(
  message: DetectMessage | CleanMessage
): Promise<DetectionResult> {
  return new Promise((resolve, reject) => {
    if (!workerInitialized) {
      initWorker();
    }

    if (!worker) {
      reject(new Error('Worker not available'));
      return;
    }

    pendingRequests.set(message.requestId, { resolve, reject });

    // Set timeout to prevent hanging requests
    setTimeout(() => {
      const pending = pendingRequests.get(message.requestId);
      if (pending) {
        pending.reject(new Error('Worker timeout'));
        pendingRequests.delete(message.requestId);
      }
    }, 10000); // 10 second timeout

    worker.postMessage(message);
  });
}

/**
 * Generate a unique request ID
 */
function generateRequestId(): string {
  return `req_${++requestIdCounter}_${Date.now()}`;
}

/**
 * React component for rendering watermark indicators
 */
const WatermarkIndicator: React.FC<{ char: string; name: string }> = ({
  char,
  name,
}) => (
  <span
    className="inline-flex items-center gap-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-[10px] leading-none px-1 py-0.5 rounded-full mx-0.5 whitespace-nowrap"
    title={`Hidden character detected: ${name}`}
  >
    <AlertCircle size={10} className="shrink-0" />
    <span className="font-medium">{name}</span>
  </span>
);

/**
 * Detect watermarks using Web Worker (ASYNC)
 *
 * This function sends the text to a Web Worker for processing.
 * ALL heavy work happens off the main thread.
 *
 * @returns Promise with highlighted output, count, and modified text
 */
export async function detectWatermarks(
  text: string,
  hasPremium: boolean = false,
  wasPasted: boolean = false
): Promise<{
  highlightedOutput: React.ReactNode;
  count: number;
  modifiedText?: string;
}> {
  if (!text) {
    return { highlightedOutput: null, count: 0 };
  }

  // Detect watermarks via worker
  const result = await sendWorkerMessage({
    type: 'DETECT',
    text,
    hasPremium,
    wasPasted,
    requestId: generateRequestId(),
  });

  // If no watermarks found
  if (result.count === 0) {
    return {
      highlightedOutput: (
        <div className="whitespace-pre-wrap break-words text-neutral-700 dark:text-neutral-300">
          {result.modifiedText || text}
        </div>
      ),
      count: 0,
      modifiedText: result.modifiedText,
    };
  }

  // Build React elements from segments
  const segments = result.segments.map((segment, idx) => {
    if (segment.type === 'text') {
      return segment.content;
    } else {
      return (
        <WatermarkIndicator
          key={`watermark-${segment.index || idx}`}
          char={segment.char!}
          name={segment.name!}
        />
      );
    }
  });

  return {
    highlightedOutput: (
      <div className="whitespace-pre-wrap break-words text-neutral-700 dark:text-neutral-300">
        {segments}
      </div>
    ),
    count: result.count,
    modifiedText: result.modifiedText,
  };
}

/**
 * Remove all watermark characters from the input text (ASYNC)
 *
 * This function sends the text to a Web Worker for cleaning.
 * Uses the worker for consistency, even though cleaning is simpler.
 *
 * @param text - The text to clean
 * @param dashHandling - Options for how to handle dashes (mode and optional custom character)
 */
export async function removeWatermarks(text: string, dashHandling?: DashHandlingOptions): Promise<string> {
  if (!text) return '';

  const result = await sendWorkerMessage({
    type: 'CLEAN',
    text,
    requestId: generateRequestId(),
    dashHandling,
  });

  return result.cleanedText;
}

/**
 * Initialize worker on module load (client-side only)
 */
if (typeof window !== 'undefined') {
  // Lazy init - worker will be created on first use
  // This prevents SSR issues
}
